import { useHistory, useLocation } from 'react-router';
import { useFlowsSelector } from '../../../common/src/utils/globalVariables';
import { FlowsQuestionnaire } from '../types/FlowsTypes';
import useCurrentGroup from './useCurrentGroup';
import { useFlowsRouteParams } from './useFlowsRouteParams';

interface ToolbarItemProps {
  disabled: boolean;
  onClick: () => void;
  inProgress: boolean;
  isCompleted: boolean;
}

interface ToolbarItems {
  [tag: string]: number;
}

interface ReturnProps {
  itemList: ToolbarItems;
  getItemProps: (tag: string, index: number) => ToolbarItemProps;
}

const useAdvisoryFlowToolbar = (): ReturnProps => {
  const history = useHistory();
  const location = useLocation();
  const currentGroup = useCurrentGroup();
  const match = useFlowsRouteParams();
  const visitedTags = useFlowsSelector(
    (state) => state.flows.metaData.visitedTags,
  );
  const flowsQuestionnaire = useFlowsSelector(
    (state) => state.flows.questionnaire,
  );

  const getItemList = (questionnaire: FlowsQuestionnaire) =>
    Object.assign(
      {},
      ...questionnaire.groups.map(({ tag }, index: number) => ({
        [tag]: index,
      })),
    );

  const itemList = getItemList(flowsQuestionnaire);

  const getIsCompleted = (tag: string) => {
    // If tag was visited and is not the last one, it's completed
    if (
      visitedTags.includes(tag) &&
      visitedTags[visitedTags.length - 1] !== tag
    ) {
      return true;
      // If on finalize route, all is done
    }
    return !location.pathname.includes('/flows') && visitedTags.includes(tag);
  };

  const handleTagClick = (index: number): void => {
    const { flowId, sessionId } = match;
    if (!flowId || !sessionId) return;
    const tagPosition = index === 0 ? 0 : itemList[visitedTags[index - 1]] + 1;
    return history.push(
      `/flows/${flowId}/questions/${sessionId}/${tagPosition}`,
    );
  };

  const getItemProps = (tag: string, index: number): ToolbarItemProps => {
    return {
      isCompleted: getIsCompleted(tag),
      disabled:
        !visitedTags.includes(tag) ||
        (!location.pathname.includes('/flows') && visitedTags.includes(tag)),
      onClick: () => handleTagClick(index),
      inProgress: tag === currentGroup.tag,
    };
  };

  return { getItemProps, itemList };
};

export default useAdvisoryFlowToolbar;
